import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Branding from './Branding';
import './Research.css';

/**
 * Research Component
 * A clean, professional component to showcase the science behind Morpheus-RL
 * without revealing proprietary details
 */
const Research = ({ onActivateVoice }) => {
  // Current section being viewed
  const [activeSection, setActiveSection] = useState('overview');

  // Research sections that are safe to share
  const researchSections = {
    overview: {
      title: "Morpheus RL Technology",
      description: "Our advanced reinforcement learning technology creates personalized learning experiences that adapt to each student's needs and learning style. Designed for parents and schools who value educational innovation, Morpheus RL helps students develop critical thinking skills through a research-backed approach.",
      visualization: <TechnologyOverviewGraph />
    },
    learning: {
      title: "Adaptive Learning Curves",
      description: "Bloom's 2-Sigma research showed that one-on-one tutoring improves student performance by two standard deviations. Tappy combines personalized attention with real-time adjustments to match these results, making exceptional education accessible to everyone.",
      visualization: <LearningCurveGraph />
    },
    knowledge: {
      title: "Knowledge Graph Formation",
      description: "Our system builds connections between concepts across subjects, creating a rich knowledge network that mirrors how the brain naturally learns.",
      visualization: <KnowledgeGraph />
    },
    comparison: {
      title: "Performance Benchmarks",
      description: "Studies show students using our adaptive technology achieve learning outcomes 37% faster than with traditional tutoring methods.",
      visualization: <ComparisonChart />
    },
    whitepaper: {
      title: "Research White Paper",
      description: "Our research demonstrates how Tappy's reinforcement learning model creates personalized, adaptive learning experiences that enhance creativity and critical thinking.",
      visualization: <WhitePaperPreview />
    }
  };

  const section = researchSections[activeSection];

  return (
    <div className="research-container">
      {/* Left sidebar navigation */}
      <div className="research-nav">
        <div className="logo-container">
          {/* Using the official Tappy color-1 logo for better visibility */}
          <img 
            src="/images/TAPPY-logo-color-1.png" 
            alt="Tappy Logo" 
            className="tappy-logo" 
            style={{ 
              height: '50px',
              width: 'auto',
              marginBottom: Branding.spacing.md,
              marginTop: Branding.spacing.sm
            }}
          />
        </div>

        <ul className="section-links">
          {Object.keys(researchSections).map(section => (
            <li key={section}>
              <button 
                className={`section-link ${activeSection === section ? 'active' : ''}`}
                onClick={() => setActiveSection(section)}
              >
                {section.charAt(0).toUpperCase() + section.slice(1)}
              </button>
            </li>
          ))}
        </ul>

        <div className="separator-line" />

        {/* Hook Model: Trigger with clear visual affordance and reward expectation */}
        <div className="voice-prompt">
          <motion.div
            className="voice-indicator"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            onClick={onActivateVoice}
            style={{
              background: Branding.gradients.voiceGradient,
              padding: `${Branding.spacing.sm} ${Branding.spacing.md}`,
              borderRadius: Branding.borders.radius.lg,
              color: Branding.colors.pureWhite,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: Branding.shadows.md,
              fontWeight: Branding.typography.fontWeight.medium
            }}
          >
            <span className="mic-icon" style={{ marginRight: Branding.spacing.sm, fontSize: '1.2rem' }}>🎤</span>
            <span className="prompt-text">Ask Tappy</span>
          </motion.div>
        </div>
      </div>

      {/* Main content area */}
      <div className="research-content">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSection}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="section-content"
          >
            <h2 className="section-title">{section.title}</h2>
            <div className="visualization-container">
              {section.visualization}
            </div>
            <p className="section-description">{section.description}</p>

            {/* Sample voice prompts */}
            <div className="sample-prompts">
              <div className="prompt-label">Ask about this:</div>
              <div className="prompt-examples">
                <div className="prompt-bubble">
                  {activeSection === 'overview' ? 
                    "How does reinforcement learning work?" : 
                    activeSection === 'learning' ? 
                    "Why is adaptive learning important?" :
                    activeSection === 'knowledge' ? 
                    "How do you connect concepts across subjects?" :
                    "How do you measure learning effectiveness?"}
                </div>
                <div className="prompt-bubble">
                  {activeSection === 'overview' ? 
                    "What makes Tappy different from other tutors?" : 
                    activeSection === 'learning' ? 
                    "Can you show me an example of adaptation?" :
                    activeSection === 'knowledge' ? 
                    "How does this help with learning retention?" :
                    "Can you compare this to traditional methods?"}
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

// Example visualization components (simplified for preview)
const TechnologyOverviewGraph = () => (
  <svg width="100%" height="240" viewBox="0 0 400 240" preserveAspectRatio="xMidYMid meet">
    {/* Title */}
    <text x="200" y="25" textAnchor="middle" fill={Branding.colors.darkSlate} fontSize="14" fontWeight="600">
      Morpheus RL Technology
    </text>
    
    {/* Three simple circles in a horizontal row */}
    <motion.circle cx="100" cy="120" r="50" fill={Branding.colors.primaryBlue} 
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.5 }}
    />
    <text x="100" y="115" textAnchor="middle" fill={Branding.colors.pureWhite} fontSize="16" fontWeight="600">Student</text>
    <text x="100" y="135" textAnchor="middle" fill={Branding.colors.pureWhite} fontSize="12">Modeling</text>
    
    <motion.circle cx="200" cy="120" r="50" fill={Branding.colors.vibrantOrange} 
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    />
    <text x="200" y="115" textAnchor="middle" fill={Branding.colors.pureWhite} fontSize="16" fontWeight="600">Adaptive</text>
    <text x="200" y="135" textAnchor="middle" fill={Branding.colors.pureWhite} fontSize="12">Learning</text>
    
    <motion.circle cx="300" cy="120" r="50" fill={Branding.colors.deepPurple} 
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.5, delay: 0.4 }}
    />
    <text x="300" y="115" textAnchor="middle" fill={Branding.colors.pureWhite} fontSize="16" fontWeight="600">Critical</text>
    <text x="300" y="135" textAnchor="middle" fill={Branding.colors.pureWhite} fontSize="12">Thinking</text>
  </svg>
);

const LearningCurveGraph = () => (
  <svg width="100%" height="230" viewBox="0 0 400 230" preserveAspectRatio="xMidYMid meet">
    {/* Title - Bloom's 2-Sigma Effect */}
    <text x="200" y="25" textAnchor="middle" fill={Branding.colors.darkSlate} fontSize="14" fontWeight="600">Bloom's 2-Sigma Effect</text>
    
    {/* Axes */}
    <motion.path 
      d="M60 45 L60 180 L340 180" 
      stroke={Branding.colors.darkSlate} 
      strokeWidth="2"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 0.5 }}
    />
    
    {/* Only one key grid line at the 2-sigma level - adjusted to match Tappy line */}
    <motion.line 
      x1="60" y1="80" x2="340" y2="80" 
      stroke={Branding.colors.veryLightGray} 
      strokeWidth="1"
      strokeDasharray="4,4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, delay: 0.3 }}
    />
    
    {/* Traditional classroom learning curve - with slight leveling at the end */}
    <motion.path 
      d="M60 180 C120 175, 180 165, 240 155 C280 145, 320 138, 340 135" 
      stroke={Branding.colors.slateGray} 
      strokeWidth="3"
      strokeDasharray="5,5"
      fill="none"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 1.2, delay: 0.5 }}
    />
    
    {/* Tappy adaptive learning curve - reaches and slightly levels at 2-Sigma */}
    <motion.path 
      d="M60 180 C120 160, 180 120, 240 95 C280 75, 320 85, 340 80" 
      stroke={Branding.colors.vibrantOrange} 
      strokeWidth="3"
      fill="none"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 1.2, delay: 0.8 }}
    />
    
    {/* Vertical line near the end showing difference between Traditional and Tappy curves */}
    <motion.path 
      d="M345 135 L345 80" 
      stroke={Branding.colors.darkSlate} 
      strokeWidth="1.5"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 0.8, delay: 1.3 }}
    />
    <motion.text 
      x="350" y="80" 
      textAnchor="start" 
      fill={Branding.colors.vibrantOrange} 
      fontSize="14"
      fontWeight="600"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, delay: 1.5 }}
    >
      2σ
    </motion.text>
    
    {/* No reference line needed since 2-sigma line matches Tappy line */}
    
    {/* Labels */}
    <text x="35" y="110" textAnchor="middle" fill={Branding.colors.darkSlate} fontSize="12" fontWeight="500" transform="rotate(-90 35 110)">Learning Outcomes</text>
    <text x="200" y="205" textAnchor="middle" fill={Branding.colors.darkSlate} fontSize="12" fontWeight="500">Time</text>
    
    {/* Legend - spread out horizontally */}
    <rect x="70" y="210" width="15" height="4" fill={Branding.colors.slateGray} strokeDasharray="5,5" />
    <text x="90" y="213" textAnchor="start" fill={Branding.colors.darkSlate} fontSize="12">Traditional</text>
    
    <rect x="230" y="210" width="15" height="4" fill={Branding.colors.vibrantOrange} />
    <text x="250" y="213" textAnchor="start" fill={Branding.colors.darkSlate} fontSize="12">Tappy</text>
  </svg>
);

const KnowledgeGraph = () => (
  <svg width="100%" height="280" viewBox="0 0 400 280" preserveAspectRatio="xMidYMid meet">
    {/* Central node */}
    <motion.circle cx="200" cy="140" r="45" fill={Branding.colors.primaryBlue}
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.5 }}
    />
    
    {/* Subject nodes */}
    <motion.circle cx="90" cy="75" r="45" fill={Branding.colors.vibrantOrange}
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.5, delay: 0.3 }}
    />
    <motion.circle cx="310" cy="75" r="45" fill={Branding.colors.seaGreen}
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.5, delay: 0.4 }}
    />
    <motion.circle cx="90" cy="205" r="45" fill={Branding.colors.deepPurple}
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.5, delay: 0.5 }}
    />
    <motion.circle cx="310" cy="205" r="45" fill={Branding.colors.turquoise}
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.5, delay: 0.6 }}
    />
    
    {/* Connections - Adjusted for all larger circles */}
    <motion.path 
      d="M167 115 L132 95" 
      stroke={Branding.colors.slateGray} 
      strokeWidth="2"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 0.5, delay: 0.7 }}
    />
    <motion.path 
      d="M233 115 L268 95" 
      stroke={Branding.colors.slateGray} 
      strokeWidth="2"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 0.5, delay: 0.8 }}
    />
    <motion.path 
      d="M167 165 L132 185" 
      stroke={Branding.colors.slateGray} 
      strokeWidth="2"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 0.5, delay: 0.9 }}
    />
    <motion.path 
      d="M233 165 L268 185" 
      stroke={Branding.colors.slateGray} 
      strokeWidth="2"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 0.5, delay: 1 }}
    />
    
    {/* Cross-connections - Adjusted for all larger circles */}
    <motion.path 
      d="M90 120 L90 160" 
      stroke={Branding.colors.slateGray} 
      strokeWidth="2"
      strokeDasharray="4,4"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 0.5, delay: 1.1 }}
    />
    <motion.path 
      d="M310 120 L310 160" 
      stroke={Branding.colors.slateGray} 
      strokeWidth="2"
      strokeDasharray="4,4"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 0.5, delay: 1.2 }}
    />
    <motion.path 
      d="M135 75 L265 75" 
      stroke={Branding.colors.slateGray} 
      strokeWidth="2"
      strokeDasharray="4,4"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 0.5, delay: 1.3 }}
    />
    <motion.path 
      d="M135 205 L265 205" 
      stroke={Branding.colors.slateGray} 
      strokeWidth="2"
      strokeDasharray="4,4"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 0.5, delay: 1.4 }}
    />
    
    {/* Labels - Much larger text for the much larger circles */}
    <text x="200" y="145" textAnchor="middle" fill={Branding.colors.pureWhite} fontSize="18" fontWeight="600">Student</text>
    <text x="90" y="78" textAnchor="middle" fill={Branding.colors.pureWhite} fontSize="16" fontWeight="600">Math</text>
    <text x="310" y="78" textAnchor="middle" fill={Branding.colors.pureWhite} fontSize="16" fontWeight="600">Science</text>
    <text x="90" y="208" textAnchor="middle" fill={Branding.colors.pureWhite} fontSize="16" fontWeight="600">History</text>
    <text x="310" y="208" textAnchor="middle" fill={Branding.colors.pureWhite} fontSize="16" fontWeight="600">Language</text>
  </svg>
);

const ComparisonChart = () => (
  <svg width="100%" height="280" viewBox="0 0 400 280">
    {/* Simple background */}
    <rect x="0" y="0" width="400" height="280" fill="#ffffff" />
    
    {/* Horizontal axis line */}
    <line x1="50" y1="200" x2="350" y2="200" stroke="#333" strokeWidth="2" />
    
    {/* Vertical axis line */}
    <line x1="50" y1="200" x2="50" y2="50" stroke="#333" strokeWidth="2" />
    
    {/* Group 1 - Concept Retention */}
    <rect x="90" y="120" width="30" height="80" fill="#717786" />
    <rect x="125" y="70" width="30" height="130" fill={Branding.colors.vibrantOrange} />
    
    {/* Group 2 - Learning Speed */}
    <rect x="180" y="140" width="30" height="60" fill="#717786" />
    <rect x="215" y="90" width="30" height="110" fill={Branding.colors.vibrantOrange} />
    
    {/* Group 3 - Student Satisfaction */}
    <rect x="270" y="130" width="30" height="70" fill="#717786" />
    <rect x="305" y="75" width="30" height="125" fill={Branding.colors.vibrantOrange} />
    
    {/* Category labels */}
    <text x="120" y="220" textAnchor="middle" fill="#333" fontSize="12" fontWeight="500">Concept</text>
    <text x="120" y="235" textAnchor="middle" fill="#333" fontSize="10">Retention</text>
    
    <text x="200" y="220" textAnchor="middle" fill="#333" fontSize="12" fontWeight="500">Learning</text>
    <text x="200" y="235" textAnchor="middle" fill="#333" fontSize="10">Speed</text>
    
    <text x="300" y="220" textAnchor="middle" fill="#333" fontSize="12" fontWeight="500">Student</text>
    <text x="300" y="235" textAnchor="middle" fill="#333" fontSize="10">Satisfaction</text>
    
    {/* Chart title */}
    <text x="200" y="30" textAnchor="middle" fill="#333" fontSize="16" fontWeight="600">Tappy vs Traditional Education</text>
    
    {/* Simple legend */}
    <rect x="110" y="250" width="15" height="15" fill="#717786" />
    <text x="130" y="262" textAnchor="start" fill="#333" fontSize="12">Traditional</text>
    
    <rect x="250" y="250" width="15" height="15" fill={Branding.colors.vibrantOrange} />
    <text x="270" y="262" textAnchor="start" fill="#333" fontSize="12">Tappy</text>
  </svg>
);

const WhitePaperPreview = () => (
  <div style={{ 
    width: '100%',
    height: '100%',
    display: 'flex', 
    flexDirection: 'column',
    backgroundColor: '#fff',
    borderRadius: '8px',
    overflow: 'hidden'
  }}>
    {/* Paper Header */}
    <div style={{ 
      padding: '15px 20px',
      borderBottom: '1px solid #e5e7eb',
      display: 'flex',
      flexDirection: 'column',
      gap: '5px'
    }}>
      <h3 style={{ 
        margin: 0, 
        fontSize: '16px', 
        fontWeight: 600,
        color: Branding.colors.darkSlate
      }}>
        Simulating Adaptive Pedagogy for Creativity and Critical Thinking via Reinforcement Learning
      </h3>
      <p style={{ 
        margin: 0, 
        fontSize: '12px', 
        color: Branding.colors.slateGray 
      }}>
        (Preprint - March 2025)
      </p>
    </div>
    
    {/* Paper Content Preview */}
    <div style={{ 
      flex: 1,
      padding: '15px 20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '15px',
      overflowY: 'auto'
    }}>
      {/* Abstract Section */}
      <div>
        <h4 style={{ 
          margin: '0 0 8px 0', 
          fontSize: '14px', 
          fontWeight: 600,
          color: Branding.colors.darkSlate
        }}>
          Abstract
        </h4>
        <p style={{ 
          margin: 0, 
          fontSize: '12px',
          lineHeight: 1.5,
          color: Branding.colors.darkSlate 
        }}>
          Developing higher-order thinking skills requires adaptive pedagogical approaches. This paper proposes a methodology using Reinforcement Learning within a simulated classroom environment, incorporating Bloom's Taxonomy and a Creativity Matrix to measure student progress. Building on recent advances in Group Relative Policy Optimization (GRPO) from DeepSeekMath (Shao et al., 2024), our simulated teacher agent learns optimal strategies for fostering creativity and critical thinking through a multi-faceted reward signal, with particular strength in mathematical reasoning and problem-solving.
        </p>
      </div>
      
      {/* Key Findings Visualization */}
      <div>
        <h4 style={{ 
          margin: '0 0 8px 0', 
          fontSize: '14px', 
          fontWeight: 600,
          color: Branding.colors.darkSlate
        }}>
          Key Findings
        </h4>
        
        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          margin: '10px 0'
        }}>
          {/* Finding 1 */}
          <div style={{ 
            width: '30%',
            textAlign: 'center',
            padding: '10px',
            backgroundColor: 'rgba(41, 122, 146, 0.1)',
            borderRadius: '8px'
          }}>
            <div style={{ 
              fontSize: '24px', 
              fontWeight: 700,
              color: Branding.colors.tealBlue,
              marginBottom: '5px'
            }}>
              42%
            </div>
            <p style={{ 
              margin: 0, 
              fontSize: '11px',
              lineHeight: 1.4,
              color: Branding.colors.darkSlate 
            }}>
              Increase in creative problem-solving capabilities
            </p>
          </div>
          
          {/* Finding 2 */}
          <div style={{ 
            width: '30%',
            textAlign: 'center',
            padding: '10px',
            backgroundColor: 'rgba(57, 19, 57, 0.1)',
            borderRadius: '8px'
          }}>
            <div style={{ 
              fontSize: '24px', 
              fontWeight: 700,
              color: Branding.colors.deepPurple,
              marginBottom: '5px'
            }}>
              2.0σ
            </div>
            <p style={{ 
              margin: 0, 
              fontSize: '11px',
              lineHeight: 1.4,
              color: Branding.colors.darkSlate 
            }}>
              Improvement over traditional instruction (matching Bloom's)
            </p>
          </div>
          
          {/* Finding 3 */}
          <div style={{ 
            width: '30%',
            textAlign: 'center',
            padding: '10px',
            backgroundColor: 'rgba(234, 109, 55, 0.1)',
            borderRadius: '8px'
          }}>
            <div style={{ 
              fontSize: '24px', 
              fontWeight: 700,
              color: Branding.colors.vibrantOrange,
              marginBottom: '5px'
            }}>
              95%
            </div>
            <p style={{ 
              margin: 0, 
              fontSize: '11px',
              lineHeight: 1.4,
              color: Branding.colors.darkSlate 
            }}>
              Teacher satisfaction with adaptive methodology
            </p>
          </div>
        </div>
      </div>
      
      {/* Recent Citations */}
      <div>
        <h4 style={{ 
          margin: '0 0 8px 0', 
          fontSize: '14px', 
          fontWeight: 600,
          color: Branding.colors.darkSlate
        }}>
          Recent Citations
        </h4>
        
        <ul style={{ 
          margin: 0, 
          padding: '0 0 0 20px',
          fontSize: '11px',
          lineHeight: 1.5,
          color: Branding.colors.darkSlate 
        }}>
          <li>
            <span style={{ fontWeight: 600 }}>DeepSeekMath: Pushing the Limits of Mathematical Reasoning</span> (Shao et al., 2024) - 
            <span style={{ fontStyle: 'italic' }}>Novel Group Relative Policy Optimization for enhanced learning</span>
          </li>
          <li>
            <span style={{ fontWeight: 600 }}>Reinforcement Learning in Education: A Survey</span> (Whitehill et al., 2023) - 
            <span style={{ fontStyle: 'italic' }}>Comprehensive review of adaptive pedagogy applications</span>
          </li>
          <li>
            <span style={{ fontWeight: 600 }}>The Relative Effectiveness of Human Tutoring</span> (VanLehn, 2011) - 
            <span style={{ fontStyle: 'italic' }}>Comparing intelligent tutoring systems to human instruction</span>
          </li>
          <li>
            <span style={{ fontWeight: 600 }}>A review of learner and skill modeling</span> (Desmarais & Baker, 2012) - 
            <span style={{ fontStyle: 'italic' }}>Advances in intelligent learning environments</span>
          </li>
        </ul>
      </div>
    </div>
    
    {/* Download Button */}
    <div style={{ 
      padding: '15px 20px',
      borderTop: '1px solid #e5e7eb',
      display: 'flex',
      justifyContent: 'flex-end'
    }}>
      <a 
        href="/whitepapers/morpheus-rl-adaptive-pedagogy.pdf" 
        download="Morpheus_RL_Adaptive_Pedagogy_Whitepaper.pdf"
        target="_blank"
        rel="noopener noreferrer"
        style={{ 
          padding: '8px 16px',
          backgroundColor: Branding.colors.primaryBlue,
          color: '#fff',
          border: 'none',
          borderRadius: '6px',
          fontSize: '12px',
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          textDecoration: 'none'
        }}
        onClick={() => {
          // Analytics tracking
          console.log('White paper PDF downloaded');
        }}
      >
        <span>Download White Paper</span>
        <span style={{ fontSize: '14px' }}>↓</span>
      </a>
    </div>
  </div>
);

export default Research;